import type { NextSeoProps } from "next-seo";
import { NextSeo } from "next-seo";
import { usePathname } from "next/navigation";

import type { AppImageProps, MeetingImageProps } from "@calcom/lib/OgImages";
import { constructAppImage, constructGenericImage, constructMeetingImage } from "@calcom/lib/OgImages";
import { APP_NAME, WEBSITE_URL, IS_CALCOM } from "@calcom/lib/constants";
import { seoConfig, getSeoImage, buildCanonical } from "@calcom/lib/next-seo.config";
import { getTenantConfig } from "@calcom/lib/tenantConfig";
import { truncateOnWord } from "@calcom/lib/text";

export type HeadSeoProps = {
  title: string;
  description: string;
  siteName?: string;
  url?: string;
  canonical?: string;
  nextSeoProps?: NextSeoProps;
  app?: AppImageProps;
  meeting?: MeetingImageProps;
  isBrandingHidden?: boolean;
  origin?: string; // 👈 Add origin prop
};

const buildSeoMeta = (pageProps: {
  title: string;
  description: string;
  image: string;
  siteName?: string;
  url?: string;
  canonical?: string;
}) => {
  const { title, description, image, canonical, siteName = seoConfig.headSeo.siteName } = pageProps;
  return {
    title,
    canonical,
    openGraph: {
      site_name: siteName,
      type: "website",
      title,
      description,
      images: [{ url: image }],
    },
    additionalMetaTags: [
      { property: "name", content: title },
      { property: "description", content: description },
      { name: "description", content: description },
      { property: "image", content: image },
    ],
  };
};

export const HeadSeo = (props: HeadSeoProps): JSX.Element => {
  const path = usePathname();

  // Determine origin dynamically
  const origin =
    typeof window !== "undefined"
      ? window.location.origin // always use actual browser origin if available
      : props.origin || WEBSITE_URL || "https://cal.mtesthub.com"; // fallback for SSR

  console.log("[HeadSeo] Resolved origin:", origin);

  const tenantConfig = getTenantConfig(origin);
  console.log("[HeadSeo] Tenant config for origin:", tenantConfig);

  const selfHostedOrigin = origin || "https://cal.com";
  const defaultUrl = IS_CALCOM
    ? buildCanonical({ path, origin: "https://cal.com" })
    : buildCanonical({ path, origin: selfHostedOrigin });
  console.log("[HeadSeo] Default canonical URL:", defaultUrl);

  const {
    title,
    description,
    siteName,
    canonical = defaultUrl,
    nextSeoProps = {},
    app,
    meeting,
    isBrandingHidden,
  } = props;

  const effectiveSiteName = tenantConfig?.title || siteName;
  const effectiveAppName = tenantConfig?.title || APP_NAME;

  const truncatedDescription = truncateOnWord(description, 158);
  const pageTitle = `${title}`;

  let image = getSeoImage("ogImage") + constructGenericImage({ title, description: truncatedDescription });

  if (meeting) {
    image = getSeoImage("ogImage") + constructMeetingImage(meeting);
  } else if (app) {
    image = getSeoImage("ogImage") + constructAppImage({ ...app, description: truncatedDescription });
  }

  const seoObject = buildSeoMeta({
    title: pageTitle,
    description: truncatedDescription,
    image,
    canonical,
    siteName: effectiveSiteName,
  });

  console.log("[HeadSeo] SEO object:", seoObject);

  const seoProps: NextSeoProps = {
    ...nextSeoProps,
    ...seoObject,
    openGraph: {
      ...nextSeoProps.openGraph,
      ...seoObject.openGraph,
      images: [...(nextSeoProps.openGraph?.images || []), ...seoObject.openGraph.images],
    },
    additionalMetaTags: [...(nextSeoProps.additionalMetaTags || [])],
  };

  console.log("[HeadSeo] Final NextSeo props:", seoProps);

  return <NextSeo {...seoProps} />;
};

export default HeadSeo;
